import { Typography, Chip, Box } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useMemo, useState } from 'react'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { AppSkeleton } from 'src/component/common/analog/molecule/AppSkelton'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { Palette } from 'src/feature/theme/model/Palette'
import { RatingChart } from '../../model/my-team-skill-gaps'

export type DonutChartProps = {
  chartData?: RatingChart[]
  isLoading?: boolean
  onRatingChange?: (rating: number) => void
  onItemClick?: (itemName: string) => void
}

type TooltipProps = {
  active?: boolean
  payload?: Array<{ payload: { name: string; value: number; expectedValue: number } }>
}

const RATING_OPTIONS = [1, 2, 3, 4, 5] as const
const CHART_HEIGHT = 200
const DONUT_INNER_RADIUS = 60
const DONUT_OUTER_RADIUS = 90

function CustomTooltip({ active, payload }: TooltipProps) {
  const { translation } = useLocalizationStore()
  const pageTranslation = translation.pages.myTeam.donutChart

  if (active && payload && payload.length) {
    const data = payload[0].payload

    return (
      <AppBox sx={styles.tooltip}>
        <Typography sx={styles.tooltipTitle}>{data.name}</Typography>
        <Typography sx={styles.tooltipValue}>
          {pageTranslation.rating()}: {data.value.toFixed(2)}
        </Typography>
        {data.expectedValue > 0 && (
          <Typography sx={styles.tooltipDetail}>
            {pageTranslation.current()}: {data.value.toFixed(2)} / {pageTranslation.expected()}: {data.expectedValue.toFixed(2)}
          </Typography>
        )}
      </AppBox>
    )
  }

  return null
}

export const DonutChart = observer(({ chartData, isLoading = false, onRatingChange, onItemClick }: DonutChartProps) => {
  const { translation } = useLocalizationStore()
  const pageTranslation = translation.pages.myTeam.donutChart
  const [minRating, setMinRating] = useState(3)

  const handleRatingChange = (rating: number) => {
    setMinRating(rating)
    onRatingChange?.(rating)
  }

  const getColor = (rating: number): string => {
    if (rating >= 4) {
      return Palette.OLIVE_GREEN
    }
    if (rating >= 2) {
      return Palette.GOLDEN_ORANGE
    }

    return Palette.DANGER
  }

  const skills = chartData || []
  const filteredData = skills.filter((item) => {
    const rating = item.avgSkillRating || 0
    
    return rating >= minRating && rating < minRating + 1
  })
  const totalItems = filteredData.length

  const chartDataFormatted = useMemo(() => {
    return filteredData.map(item => ({
      name: item.name,
      value: item.avgSkillRating || 0,
      expectedValue: item.avgExpectedSkillRating || 0,
      color: getColor(item.avgSkillRating || 0),
    }))
  }, [filteredData])

  if (isLoading) {
    return (
      <AppPaper sx={styles.root}>
        <AppSkeleton
          count={2}
          direction="column"
          rects={[
            { width: '100%', height: 60, color: 'background.dark' },
            { width: '100%', height: CHART_HEIGHT, color: 'background.dark' },
          ]}
        />
      </AppPaper>
    )
  }

  return (
    <AppPaper sx={styles.root}>
      <AppBox sx={styles.filterContainer}>
        <Typography sx={styles.filterTitle}>{pageTranslation.filterByRating()}</Typography>
        <AppBox sx={styles.chipsContainer}>
          {RATING_OPTIONS.map(rating => (
            <Chip
              key={rating}
              label={
                <AppBox sx={styles.chipContent}>
                  <Typography sx={{ ...styles.chipRating, color: minRating === rating ? 'primary.contrastText' : 'inherit' }}>{rating}</Typography>
                  <Typography sx={{ ...styles.chipStar, color: minRating === rating ? 'primary.contrastText' : 'inherit' }}>★</Typography>
                </AppBox>
              }
              onClick={() => handleRatingChange(rating)}
              variant={minRating === rating ? 'filled' : 'outlined'}
              color={minRating === rating ? 'primary' : 'default'}
              sx={{
                ...styles.chip,
                '&.MuiChip-filled': {
                  backgroundColor: getColor(rating),
                  color: 'primary.contrastText',
                },
              }}
            />
          ))}
        </AppBox>
        <Typography sx={styles.filterDescription}>
          {pageTranslation.showing()} {filteredData.length} {filteredData.length === 1 ? pageTranslation.skill() : pageTranslation.skills()} {pageTranslation.rated()} <Box component="span" sx={styles.ratingValue}>{minRating}★</Box>
        </Typography>
      </AppBox>
      <AppBox sx={styles.chartWrapper}>
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <PieChart>
            <Pie
              data={chartDataFormatted}
              cx="50%"
              cy="50%"
              innerRadius={DONUT_INNER_RADIUS}
              outerRadius={DONUT_OUTER_RADIUS}
              paddingAngle={2}
              dataKey="value"
              onClick={data => onItemClick?.(data.name)}
              style={{ cursor: onItemClick ? 'pointer' : 'default' }}
            >
              {chartDataFormatted.map(entry => (
                <Cell key={`cell-${entry.name}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        <AppBox sx={styles.centerContent}>
          <Typography sx={styles.totalValue}>{totalItems}</Typography>
          <Typography sx={styles.totalLabel}>{pageTranslation.skills()}</Typography>
        </AppBox>
      </AppBox>
    </AppPaper>
  )
})

const styles = {
  root: {
    display: 'flex',
    width: '100%',
    padding: '10px 15px',
    borderRadius: '10px',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
  },
  filterContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    gap: 10,
  },
  filterTitle: {
    fontSize: 11,
    color: 'text.secondary',
    textAlign: 'center',
  },
  chipsContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 5,
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  chip: {
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    padding: { xs: '3px 2px', sm: '4px 2px' },
    height: 'auto',
    fontSize: { xs: 10, sm: 11 },
    fontWeight: 500,
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  },
  chipContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  chipRating: {
    fontSize: 12,
  },
  chipStar: {
    fontSize: 12,
  },
  filterDescription: {
    fontSize: 11,
    color: 'text.secondary',
    textAlign: 'center',
  },
  ratingValue: {
    fontWeight: 700,
    color: 'primary.main',
    fontSize: 12,
  },
  box: {
    width: '100%',
    display: 'flex',
    minHeight: CHART_HEIGHT,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chartWrapper: {
    position: 'relative',
    width: '100%',
    height: CHART_HEIGHT,
  },
  centerContent: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
  },
  totalValue: {
    fontSize: 32,
    fontWeight: 700,
    textDecoration: 'underline',
    color: 'text.primary',
    lineHeight: 1,
  },
  totalLabel: {
    fontSize: 11,
    color: 'text.secondary',
    textTransform: 'uppercase',
  },
  tooltip: {
    display: 'flex',
    flexDirection: 'column',
    padding: '8px 12px',
    borderRadius: '10px',
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.paper',
    gap: 2,
    maxWidth: 200,
  },
  tooltipTitle: {
    fontSize: 11,
    fontWeight: 500,
    color: 'text.primary',
  },
  tooltipValue: {
    fontSize: 11,
    color: 'error.main',
    fontWeight: 500,
  },
  tooltipDetail: {
    fontSize: 10,
    color: 'text.secondary',
  },
  noDataText: {
    color: 'text.secondary',
    fontSize: 11,
  },
}
